import React, { memo, useContext, useMemo } from "react"
import { ChevronDown } from "lucide-react"
import useMessageRows from "../hooks/useMessageRows"
import ChatHelpers from "../lib/chatHelpers"
import { XmppContext } from "../stream/xmppClient"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import useSlickAISilencePredicate from "@/hooks/useSlickAISilencePredicate"
import Chat = Types.Api.Chat

type ScrollBottomButtonProp = {
  chat: Chat
  onClick: () => void
  disabled: boolean
}

const ScrollBottomButton = ({
  chat,
  onClick,
  disabled,
}: ScrollBottomButtonProp) => {
  const { myJid } = useContext(XmppContext)
  const messages = useMessageRows(chat.jid)
  // Slick's automations are folded into one quiet line in this chat, so they
  // must not drive the badge either.
  const silenceSlickAI = useSlickAISilencePredicate()(chat.jid)
  const unreadCount = useMemo(() => {
    return ChatHelpers.unreadMessagesCount(
      { ...chat, messages: messages ?? [] },
      myJid.toString(),
      { silenceSlickAI },
    )
  }, [chat, messages, myJid, silenceSlickAI])

  return (
    <div
      data-testid="scroll-to-bottom"
      className="absolute bottom-[10px] right-5 z-10"
    >
      <div className="relative">
        {unreadCount > 0 && (
          <div className="absolute -right-1 -top-1 z-10 flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary p-1 font-semibold text-white">
            {unreadCount}
          </div>
        )}
        <Button
          variant="floating"
          size="floating"
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          className={cn(
            "shadow-lg",
            disabled && "cursor-not-allowed opacity-70",
          )}
        >
          <ChevronDown size={24} />
        </Button>
      </div>
    </div>
  )
}

export default memo(ScrollBottomButton)
